<template>
	<div
		ref="selectWrapper"
		class="ls-select-wrapper ls-select"
		:class="{
			invalid: invalid,
			disabled: disabled,
			open: isOpen,
			loading: loading,
		}"
	>
		<label v-if="label || slots.default" class="ls-select-label" :class="{ invalid }">
			<slot>{{ label }}</slot>
		</label>

		<button ref="triggerRef" type="button" class="ls-select-trigger" :class="{ invalid, open: isOpen }" @click="toggleDropdown" @keydown="handleTriggerKeydown" :disabled="disabled || loading" :tabindex="tabindex" role="combobox" :aria-expanded="isOpen" aria-haspopup="listbox" :aria-controls="listboxId" :aria-activedescendant="activeDescendantId" :aria-label="ariaLabel">
			<span class="ls-select-value" :class="{ placeholder: !hasSelection }">
				<slot name="value" :value="modelValue" :option="selectedOption">
					{{ displayValue }}
				</slot>
			</span>

			<span class="ls-select-icon" :class="{ open: isOpen }">
				<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
					<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m6 9l6 6l6-6" />
				</svg>
			</span>
		</button>

		<Teleport to="body">
			<div v-if="isOpen" ref="dropdownRef" class="ls-select-dropdown" :style="mergedDropdownStyle">
				<ul :id="listboxId" role="listbox" :aria-label="ariaLabel || label">
					<li
						v-for="(option, index) in options"
						:key="getOptionValue(option)"
						:id="`${listboxId}-option-${index}`"
						:ref="(el) => setOptionRef(el as HTMLElement | null, index)"
						role="option"
						class="ls-select-option"
						:class="{
							selected: isSelected(option),
							active: activeIndex === index,
						}"
						:aria-selected="isSelected(option)"
						:tabindex="activeIndex === index ? 0 : -1"
						@click="selectOption(option)"
						@mouseenter="activeIndex = index"
					>
						<slot name="option" :option="option" :index="index">
							<span class="ls-select-option-text">{{ getOptionLabel(option) }}</span>
						</slot>
					</li>
					<li v-if="!options || options.length === 0" class="ls-select-option empty">No options available</li>
				</ul>
			</div>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import { useOnClickOutside } from "../composables/useOnClickOutside";
import type { LSInputProps } from "../../shared/types/input";

type OptionType = any;

interface SelectProps extends /* @vue-ignore */ LSInputProps {
	modelValue?: OptionType | null;

	options: OptionType[];
	optionLabel?: string;
	optionValue?: string;

	label?: string;
	placeholder?: string;

	disabled?: boolean;
	loading?: boolean;
	invalid?: boolean;

	ariaLabel?: string;
	tabindex?: number;

	minWidth?: string | number;
	offsetX?: number;
	offsetY?: number;

	maxVisibleItems?: number;

	/** Additional inline styles applied to the dropdown element */
	dropdownStyle?: Record<string, string>;
}

const props = withDefaults(defineProps<SelectProps>(), {
	modelValue: null,
	options: () => [],
	optionLabel: "label",
	optionValue: "value",
	label: "",
	placeholder: "Select...",
	disabled: false,
	loading: false,
	invalid: false,
	tabindex: 0,
	offsetX: 0,
	offsetY: 0,
	maxVisibleItems: 6,
});

const emit = defineEmits<{
	(e: "update:modelValue", value: OptionType | null): void;
	(e: "change", value: OptionType | null): void;
	(e: "beforeShow"): void;
	(e: "beforeHide"): void;
}>();

const slots = useSlots();

const selectWrapper = useTemplateRef<HTMLElement>("selectWrapper");
const triggerRef = useTemplateRef<HTMLButtonElement>("triggerRef");
const dropdownRef = useTemplateRef<HTMLElement>("dropdownRef");

const isOpen = ref(false);
const activeIndex = ref(-1);
const optionRefs = ref<Map<number, HTMLElement>>(new Map());
const dropdownStyle = ref<Record<string, string>>({});
const mergedDropdownStyle = computed(() => ({
	...dropdownStyle.value,
	...props.dropdownStyle,
}));

const componentId = useId();
const listboxId = computed(() => `ls-select-listbox-${componentId}`);

const OPTION_HEIGHT = 40; // Approximate height of each option in pixels
const DROPDOWN_PADDING = 8; // ul padding (0.25rem * 2)

const dropdownMaxHeight = computed(() => {
	return props.maxVisibleItems * OPTION_HEIGHT + DROPDOWN_PADDING;
});

const activeDescendantId = computed(() => (activeIndex.value >= 0 ? `${listboxId.value}-option-${activeIndex.value}` : undefined));

function setOptionRef(el: HTMLElement | null, index: number) {
	if (el) {
		optionRefs.value.set(index, el);
	} else {
		optionRefs.value.delete(index);
	}
}

function getOptionLabel(option: OptionType): string {
	if (typeof option === "string" || typeof option === "number") {
		return String(option);
	}
	const obj = option as Record<string, any>;
	return props.optionLabel ? String(obj[props.optionLabel] ?? "") : String(obj.label ?? "");
}

function getOptionValue(option: OptionType): string | number {
	if (typeof option === "string" || typeof option === "number") {
		return option;
	}
	const obj = option as Record<string, any>;
	return props.optionValue ? obj[props.optionValue] : obj.value;
}

function getModelValueKey(): string | number | null {
	if (props.modelValue === null || props.modelValue === undefined) return null;
	if (typeof props.modelValue === "string" || typeof props.modelValue === "number") {
		return props.modelValue;
	}
	return getOptionValue(props.modelValue);
}

const selectedOption = computed(() => {
	const key = getModelValueKey();
	if (key === null) return null;
	return props.options.find((opt) => getOptionValue(opt) === key) ?? null;
});

function isSelected(option: OptionType): boolean {
	return getOptionValue(option) === getModelValueKey();
}

const hasSelection = computed(() => props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== "");

const displayValue = computed(() => {
	if (!hasSelection.value) {
		return props.placeholder;
	}
	if (selectedOption.value) {
		return getOptionLabel(selectedOption.value);
	}
	return String(props.modelValue);
});

function updateDropdownPosition() {
	if (!triggerRef.value) return;

	const rect = triggerRef.value.getBoundingClientRect();
	const viewportHeight = window.innerHeight;
	const spaceBelow = viewportHeight - rect.bottom;
	const spaceAbove = rect.top;

	const dropdownHeight = dropdownMaxHeight.value;
	const openAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

	const viewportMargin = 8;
	const viewportWidth = window.innerWidth;
	let left = rect.left + props.offsetX;

	// Clamp left so dropdown stays within viewport with margin
	if (left < viewportMargin) {
		left = viewportMargin;
	} else if (left + rect.width > viewportWidth - viewportMargin) {
		left = Math.max(viewportMargin, viewportWidth - rect.width - viewportMargin);
	}

	dropdownStyle.value = {
		position: "fixed",
		left: `${left}px`,
		minWidth: `${rect.width}px`,
		maxWidth: `${viewportWidth - viewportMargin * 2}px`,
		maxHeight: `${dropdownMaxHeight.value}px`,
		zIndex: "1100",
		...(props.minWidth ? { minWidth: typeof props.minWidth === "number" ? `${props.minWidth}px` : props.minWidth } : {}),
		...(openAbove ? { bottom: `${viewportHeight - rect.top + props.offsetY}px` } : { top: `${rect.bottom + props.offsetY}px` }),
	};
}

function toggleDropdown() {
	if (props.disabled || props.loading) return;

	if (isOpen.value) {
		closeDropdown();
	} else {
		openDropdown();
	}
}

function openDropdown() {
	emit("beforeShow");
	updateDropdownPosition();
	isOpen.value = true;

	const selectedIndex = props.options.findIndex((opt) => isSelected(opt));
	activeIndex.value = selectedIndex >= 0 ? selectedIndex : 0;

	clickOutside.start();
}

function closeDropdown() {
	emit("beforeHide");
	isOpen.value = false;
	activeIndex.value = -1;
	optionRefs.value.clear();
	clickOutside.stop();
}

function selectOption(option: OptionType) {
	const value = getOptionValue(option);
	emit("update:modelValue", value);
	emit("change", value);
	closeDropdown();
	triggerRef.value?.focus();
}

function handleTriggerKeydown(event: KeyboardEvent) {
	switch (event.key) {
		case "Enter":
		case " ":
			event.preventDefault();
			if (!isOpen.value) {
				openDropdown();
			} else if (activeIndex.value >= 0 && activeIndex.value < props.options.length) {
				selectOption(props.options[activeIndex.value]!);
			}
			break;

		case "ArrowDown":
			event.preventDefault();
			if (!isOpen.value) {
				openDropdown();
			} else {
				navigateOptions(1);
			}
			break;

		case "ArrowUp":
			event.preventDefault();
			if (!isOpen.value) {
				openDropdown();
			} else {
				navigateOptions(-1);
			}
			break;

		case "Escape":
			event.preventDefault();
			if (isOpen.value) {
				closeDropdown();
			}
			break;

		case "Tab":
			if (isOpen.value) {
				closeDropdown();
			}
			break;

		case "Home":
			if (isOpen.value) {
				event.preventDefault();
				activeIndex.value = 0;
				scrollToOption(0);
			}
			break;

		case "End":
			if (isOpen.value) {
				event.preventDefault();
				activeIndex.value = props.options.length - 1;
				scrollToOption(props.options.length - 1);
			}
			break;
	}
}

function navigateOptions(direction: number) {
	if (!props.options.length) return;

	let newIndex = activeIndex.value + direction;

	if (newIndex < 0) {
		newIndex = props.options.length - 1;
	} else if (newIndex >= props.options.length) {
		newIndex = 0;
	}

	activeIndex.value = newIndex;
	scrollToOption(newIndex);
}

function scrollToOption(index: number) {
	nextTick(() => {
		const optionEl = optionRefs.value.get(index);
		if (optionEl) {
			optionEl.scrollIntoView({ block: "nearest" });
		}
	});
}

const clickOutside = useOnClickOutside(
	[selectWrapper, dropdownRef],
	() => {
		if (isOpen.value) {
			closeDropdown();
		}
	},
	{ events: ["mousedown", "touchstart"] },
);

watch(isOpen, (open) => {
	if (open) {
		window.addEventListener("scroll", updateDropdownPosition, true);
		window.addEventListener("resize", updateDropdownPosition);
	} else {
		window.removeEventListener("scroll", updateDropdownPosition, true);
		window.removeEventListener("resize", updateDropdownPosition);
	}
});

onBeforeUnmount(() => {
	clickOutside.cleanup();
	window.removeEventListener("scroll", updateDropdownPosition, true);
	window.removeEventListener("resize", updateDropdownPosition);
});

function focus() {
	if (triggerRef.value) {
		triggerRef.value.focus();
	}
}

defineExpose({
	focus,
	$refs: {
		focusInput: computed(() => triggerRef.value),
	},
	show: toggleDropdown,
});
</script>

<style scoped lang="scss">
.ls-select-wrapper {
	display: flex;
	flex-direction: column;
	position: relative;
	width: 100%;
}

.ls-select-label {
	font-weight: 500;
	color: var(--text-400);
	font-size: 15px;
	margin-bottom: 0.35rem;

	&.invalid {
		color: var(--red-500);
	}
}

.ls-select-trigger {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: var(--ls-input-height);
	padding: 0.5rem 0.8rem;
	background-color: var(--background-0);
	border: 1px solid var(--background-300);
	border-radius: 6px;
	font-size: 1rem;
	font-family: inherit;
	color: var(--text-50);
	cursor: pointer;
	box-sizing: border-box;
	text-align: left;
	transition:
		color 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
		background-color 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
		border-color 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);

	&:hover:not(:disabled) {
		border-color: var(--background-400);
	}

	&:focus {
		outline: 2px solid var(--ls-primary-color);
		outline-offset: 2px;
		border-color: var(--ls-primary-color);
	}

	&:disabled {
		background-color: var(--background-100);
		border-color: var(--background-500);
		color: var(--text-500);
		cursor: not-allowed;
	}

	&.invalid {
		border-color: var(--red-500);
		color: var(--red-500);

		&:hover,
		&:focus {
			border-color: var(--red-600);
		}
	}

	&.open {
		border-color: var(--background-700);
	}
}

.ls-select-value {
	flex: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	&.placeholder {
		color: var(--text-600);
	}
}

.ls-select-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-left: 0.5rem;
	color: var(--text-400);
	transition: transform 0.2s cubic-bezier(0.075, 0.82, 0.165, 1);

	&.open {
		transform: rotate(180deg);
	}

	svg {
		width: 16px;
		height: 16px;
	}
}

// Loading state
.ls-select-wrapper.loading {
	.ls-select-trigger {
		color: transparent;
		position: relative;
		background: linear-gradient(90deg, var(--background-50) 25%, var(--background-100) 50%, var(--background-50) 75%);
		background-size: 200% 100%;
		animation: shimmer 1.5s ease-in-out infinite;
	}
}

@media (prefers-color-scheme: dark) {
	.ls-select-trigger {
		background-color: var(--background-100);

		&.invalid {
			border-color: var(--red-600);
			color: var(--red-600);
		}
	}

	.ls-select-label {
		&.invalid {
			color: var(--red-600);
		}
	}
	.ls-select-dropdown {
		background-color: var(--background-100);
	}
}
:root[data-theme="dark"] {
	.ls-select-trigger {
		background-color: var(--background-100);

		&.invalid {
			border-color: var(--red-600);
			color: var(--red-600);
		}
	}

	.ls-select-label {
		&.invalid {
			color: var(--red-600);
		}
	}
	.ls-select-dropdown {
		background-color: var(--background-100);
	}
}

:root[data-theme="light"] {
	.ls-select-trigger {
		background-color: var(--background-0);

		&.invalid {
			border-color: var(--red-500);
			color: var(--red-500);
		}
	}

	.ls-select-label {
		&.invalid {
			color: var(--red-500);
		}
	}
	.ls-select-dropdown {
		background-color: var(--background-0);
	}
}

@keyframes shimmer {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}
</style>

<style lang="scss">
// Dropdown styles (not scoped since it's teleported)
.ls-select-dropdown {
	width: auto;
	background-color: var(--background-0);
	border: 1px solid var(--background-500);
	border-radius: 6px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	overflow-y: auto;
	margin-top: 2px;

	ul {
		list-style: none;
		margin: 0;
		padding: 0.25rem;
		gap: 0.25rem;
		display: flex;
		flex-direction: column;

		li {
			border-radius: 4px;
		}
	}
}

.ls-select-option {
	padding: 0.6rem 0.8rem;
	cursor: pointer;
	font-size: 1rem;
	color: var(--text-300);
	transition:
		background-color 0.15s ease,
		color 0.15s ease;
	font-family: var(--default-font-family);

	span {
		color: inherit;
	}

	&:hover,
	&.active {
		background-color: var(--background-300);
		color: var(--text-50);
	}

	&.selected {
		background-color: var(--ls-primary-legible-color);
		color: var(--black-50);
		font-weight: 600;
	}

	&.selected.active {
		background-color: var(--ls-primary-legible-2-color);
	}

	&.empty {
		color: var(--text-50);
		cursor: default;
		font-style: italic;

		&:hover {
			background-color: transparent;
		}
	}

	&:focus {
		outline: none;
		background-color: var(--background-100);
		color: var(--text-50);
	}

	&:focus-visible {
		outline: 2px solid var(--ls-primary-color);
		outline-offset: -2px;
	}
}

.ls-select-option-text {
	display: block;
	white-space: normal;
	word-wrap: break-word;
	line-height: 1.3;
}

.error {
	margin-top: 0.35rem;
	color: var(--red-500);
}

:root[data-theme="dark"] {
	.ls-select-dropdown {
		background-color: var(--background-100);
		border-color: var(--background-400);
	}

	.ls-select-trigger {
		border-color: var(--background-200);
	}
}

:root[data-theme="light"] {
	.ls-select-dropdown {
		background-color: var(--background-0);
	}
}
</style>
